import * as moment from 'moment';
import * as _ from 'lodash';
import {Strings} from "../../shared/config/constant.module";
import {StateParams} from "../../common";

export interface DateTimeRange {
  initialTime: moment.Moment;
  minDate: moment.Moment;
  maxDate: moment.Moment;
  minTime: string;
  maxTime: string;
}

export interface ReportTimeController {
  uiStartDate: Date;
  uiEndDate: Date;
  startDate: Date;
  endDate: Date;
}

export interface TimeRangeParams extends StateParams {
  startDate: string;
  endDate: string;
}

export class DateUtilityService {
  static formatDate(date: Date|moment.Moment, timeZone: string): string {
    if (date) {
      return moment(date)
        .tz(timeZone || Strings.DEFAULT_TIMEZONE)
        .format(Strings.DATE_FORMAT);
    }
    return '';
  }

  static formatTime(date: Date|moment.Moment, timeZone: string): string {
    return DateUtilityService.formatDateTime(date, timeZone, Strings.TIME_FORMAT);
  }

  static formatDateTime(date: Date|moment.Moment, timeZone: string, format: string): string {
    if (date) {
      var dateMoment = moment(date);
      timeZone = timeZone || Strings.DEFAULT_TIMEZONE;
      var output = dateMoment.tz(timeZone).format(format);
      if (DateUtilityService.isSecondHour(dateMoment.toDate(), timeZone)) {
        return output + ' (second hour)';
      }
      return output;
    }
    return '';
  }

  static formatTimeOnly(date: Date|moment.Moment, timeZone: string): string {
    if (date) {
      return moment(date)
        .tz(timeZone || Strings.DEFAULT_TIMEZONE)
        .format(Strings.TIME_ONLY_FORMAT);
    }
    return '';
  }

  static stringToDate(formattedDate: string, timeZone: string): moment.Moment {
    if (formattedDate) {
      var date = moment.tz(formattedDate, Strings.DATE_FORMAT, timeZone || Strings.DEFAULT_TIMEZONE);
      if (date.isValid()) {
        return date;
      }
    }
    return null;
  }

  static stringToTime(formattedDate: string, timeZone: string): moment.Moment {
    if (formattedDate) {
      var date = moment.tz(formattedDate, Strings.TIME_FORMAT, timeZone || Strings.DEFAULT_TIMEZONE);
      if (date.isValid()) {
        return date;
      }
    }
    return null;
  }

  private static localToTimeZoneWithFormat(date: Date|moment.Moment, timeZone: string, formatString: string): moment.Moment {
    if (!date) {
      return undefined;
    }

    var localDate = moment(date);
    return moment.tz(localDate.format(formatString), formatString, timeZone || Strings.DEFAULT_TIMEZONE);
  }

  static localToTimeZone(date: Date|moment.Moment, timeZone: string): moment.Moment {
    return DateUtilityService.localToTimeZoneWithFormat(date, timeZone, Strings.TIME_FORMAT);
  }

  static localToTimeZoneWithSeconds(date: Date|moment.Moment, timeZone: string): moment.Moment {
    return DateUtilityService.localToTimeZoneWithFormat(date, timeZone, Strings.TIME_FORMAT_WITH_SECONDS);
  }

  static timeZoneToLocal(date: Date|moment.Moment, timeZone: string): moment.Moment {
    if (!date) {
      return undefined;
    }

    var timeZoneDate = moment.tz(date, timeZone || Strings.DEFAULT_TIMEZONE);
    return moment(timeZoneDate.format(Strings.TIME_FORMAT), Strings.TIME_FORMAT);
  }

  static getHourRange(date: Date|moment.Moment, timeZoneId: string): DateTimeRange {
    var range;
    var now = DateUtilityService.currentMinute();
    timeZoneId = timeZoneId || Strings.DEFAULT_TIMEZONE;
    var dateMoment = moment.tz(date, timeZoneId);
    if (dateMoment.isSame(moment(now), 'hour')) {
      range = {
        initialTime: moment.tz(now, timeZoneId),
        minDate: moment(now).minutes(0),
        maxDate: moment(now)
      };
    } else {
      dateMoment = DateUtilityService.roundToMinute(dateMoment);
      range = {
        initialTime: dateMoment,
        minDate: moment(date),
        maxDate: moment(date).add(1, 'hours').subtract(1, 'minutes')
      };
    }

    range.minTime = DateUtilityService.formatISO(range.minDate, timeZoneId);
    range.maxTime = DateUtilityService.formatISO(range.maxDate, timeZoneId);
    return range;
  }

  static formatISO(date: Date|moment.Moment, timeZoneId: string): string {
    return moment(date).tz(timeZoneId || Strings.DEFAULT_TIMEZONE).format(Strings.ISO_FORMAT);
  }

  static currentMinute(): moment.Moment {
    return DateUtilityService.roundToMinute(moment());
  }

  static roundToMinute(date: moment.Moment): moment.Moment {
    return date.startOf('minute');
  }

  static getHourForTimeZone(date: Date, hour: number, timeZoneId: string): Date {
    var localDate = moment.tz(date, timeZoneId || Strings.DEFAULT_TIMEZONE).hours(hour).startOf('hour');
    return localDate.toDate();
  }

  static getTimeZoneOffset(date: Date|moment.Moment, timeZoneId: string): number {
    var tzMoment = DateUtilityService.localToTimeZone(date, timeZoneId);
    return tzMoment.zone();
  }

  static isSecondHour(date: Date, timeZoneId: string): boolean {
    var timeZoneMoment = moment.tz(date, timeZoneId);
    var previousHour = moment.tz(date, timeZoneId || Strings.DEFAULT_TIMEZONE).subtract(1, 'hours');
    return previousHour.hour() === timeZoneMoment.hour();
  }

  static setReportDates(controller: ReportTimeController,
                        params: TimeRangeParams,
                        $timeout: angular.ITimeoutService): void {
    if (params.startDate) {
      let startDate = moment(params.startDate);
      if (startDate.isValid()) {
        let date = startDate.toDate();
        controller.startDate = date;
        $timeout(function() {
          controller.startDate = date;
        });
      }
    }

    if (params.endDate) {
      let endDate = moment(params.endDate);
      if (endDate.isValid()) {
        let date = endDate.toDate();
        controller.endDate = endDate.toDate();
        $timeout(function() {
          controller.endDate = date;
        });
      }
    }
  }
}

export class TimeZoneService {
  static getTimeZones(): string[] {
    return _.filter(moment.tz.names(), (tz) => _.startsWith(tz, 'US'));
  }
}

export default DateUtilityService;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/app/services/utility/dateutility.service.ts